import InternalAuditTemplateDTO, {
  InternalAuditScheduleDTO,
  InternalAuditScheduleOccurrenceDTO,
  InternalAuditTemplateUserDTO,
} from 'models/dto/internalAuditTemplateDTO';
import { InternalAuditUser } from 'models/internalAudit';
import InternalAuditTemplate, {
  InternalAuditSchedule,
  InternalAuditScheduleOccurrence,
} from 'models/internalAuditTemplate';
import RecurringPattern from 'models/recurringPattern';
import { fromApiDateTimeOptional, fromApiDateTimeRequired } from 'utils/datetime';

export function mapFromInternalAuditTemplate(dto: InternalAuditTemplateDTO): InternalAuditTemplate {
  const item = new InternalAuditTemplate();
  item.tenantId = dto.tenantId;
  item.internalAuditTemplateId = dto.internalAuditTemplateId;
  item.name = dto.name;
  item.description = dto.description;
  item.auditType = dto.auditType;
  item.ownerId = dto.ownerId;
  item.ownerRoleId = dto.ownerRoleId;
  item.authSchemaId = dto.authSchemaId;
  item.recurrencePattern = new RecurringPattern();
  item.recurrencePattern.fromJSON(dto.recurrencePattern);
  item.taskTemplateId = dto.taskTemplateId;
  item.hasSetup = dto.hasSetup === true;
  item.setupSourceInternalAuditId = dto.setupSourceInternalAuditId;
  item.setupSavedOn = fromApiDateTimeOptional(dto.setupSavedOn);
  item.auditTrailId = dto.auditTrailId;
  item.modified = fromApiDateTimeOptional(dto.modified);
  item.users = (dto.users ?? []).map((u) => {
    const user = new InternalAuditUser();
    user.userId = u.userId;
    user.role = u.role;

    return user;
  });
  // null and absent both mean "not loaded": never coerced to [], which would read as "no standards"
  item.normIds = dto.normIds ?? undefined;

  return item;
}

export function mapFromInternalAuditTemplates(dtos: InternalAuditTemplateDTO[] | undefined): InternalAuditTemplate[] {
  if (!dtos) return [];

  return dtos.map(mapFromInternalAuditTemplate);
}

export function mapToInternalAuditTemplate(item: InternalAuditTemplate): InternalAuditTemplateDTO {
  const dto = new InternalAuditTemplateDTO();
  dto.tenantId = item.tenantId;
  dto.internalAuditTemplateId = item.internalAuditTemplateId;
  dto.name = item.name;
  dto.description = item.description;
  dto.auditType = item.auditType;
  dto.ownerId = item.ownerId;
  dto.ownerRoleId = item.ownerRoleId;
  dto.authSchemaId = item.authSchemaId;
  dto.recurrencePattern = item.recurrencePattern.toJSON();
  dto.taskTemplateId = item.taskTemplateId;
  dto.auditTrailId = item.auditTrailId;
  dto.users = item.users.map((u) => {
    const userDto = new InternalAuditTemplateUserDTO();
    userDto.internalAuditTemplateId = item.internalAuditTemplateId;
    userDto.userId = u.userId;
    userDto.role = u.role;

    return userDto;
  });
  // undefined stays undefined: the server leaves the standards untouched when they are not sent
  dto.normIds = item.normIds;

  return dto;
}

export function mapFromInternalAuditScheduleOccurrence(
  dto: InternalAuditScheduleOccurrenceDTO,
): InternalAuditScheduleOccurrence {
  const item = new InternalAuditScheduleOccurrence();
  item.internalAuditId = dto.internalAuditId;
  item.name = dto.name;
  item.status = dto.status;
  item.origin = dto.origin;
  item.startDate = fromApiDateTimeOptional(dto.startDate);
  item.endDate = fromApiDateTimeOptional(dto.endDate);
  item.scopeItemCount = dto.scopeItemCount;

  return item;
}

export function mapFromInternalAuditSchedules(dtos: InternalAuditScheduleDTO[] | undefined): InternalAuditSchedule[] {
  return (dtos ?? []).map((dto) => {
    const item = new InternalAuditSchedule();
    item.template = mapFromInternalAuditTemplate(dto.template);
    item.occurrences = (dto.occurrences ?? []).map(mapFromInternalAuditScheduleOccurrence);
    item.calculatedDates = (dto.calculatedDates ?? []).map((d) => fromApiDateTimeRequired(d));

    return item;
  });
}
